import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Edit, Eye, EyeOff, GripVertical, X, Save, Youtube, Upload, Tag } from "lucide-react";

const TALENT_CATS = [
  { key: "", label: "🌍 Toutes" },
  { key: "music", label: "🎵 Musique" }, { key: "humor", label: "😂 Humour" },
  { key: "cinema", label: "🎬 Cinéma" }, { key: "dance", label: "💃 Danse" },
  { key: "art", label: "🎨 Art" }, { key: "fashion", label: "👗 Mode" },
  { key: "food", label: "🍲 Cuisine" }, { key: "other", label: "🎤 Autre" },
];

export const AdminTopMadaVideosPanel = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // video en édition (ou {} pour nouveau)

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/topmada/videos");
      setVideos(r.data?.videos || []);
    } catch (e) {
      toast.error("Chargement échoué : " + (e?.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async (data) => {
    try {
      const url = data.video_id ? `/admin/topmada/videos/${data.video_id}` : "/admin/topmada/videos";
      await api.post(url, data);
      toast.success(data.video_id ? "Mis à jour ✨" : "Vidéo ajoutée 🎬");
      setEditing(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec"); }
  };

  const remove = async (id) => {
    if (!confirm("Supprimer cette vidéo ?")) return;
    try { await api.post(`/admin/topmada/videos/${id}/delete`); load(); toast.success("Supprimée"); }
    catch { toast.error("Échec"); }
  };

  const toggleActive = async (v) => {
    try { await api.post(`/admin/topmada/videos/${v.video_id}`, { is_active: !v.is_active }); load(); }
    catch { toast.error("Échec"); }
  };

  if (loading) return <div className="py-12 text-center"><Loader2 className="animate-spin mx-auto" /></div>;

  return (
    <div className="space-y-4" data-testid="admin-topmada-videos">
      <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-4 flex items-center justify-between gap-3">
        <div>
          <h3 className="font-bold text-lg">🎬 Clips vidéo Top Mada</h3>
          <p className="text-xs text-white/50 mt-1">
            Colle un lien YouTube / TikTok / Vimeo / Facebook. Les visiteurs verront la vidéo en évidence sur la page Top Mada.
          </p>
        </div>
        <button onClick={() => setEditing({ is_active: true, sort_order: 100 })}
                data-testid="add-topmada-video"
                className="px-3 py-2 rounded-xl bg-gradient-to-r from-amber-500 to-red-500 hover:opacity-90 font-bold text-sm flex items-center gap-2 shrink-0">
          <Plus size={14}/> Ajouter
        </button>
      </div>

      {videos.length === 0 ? (
        <div className="text-center py-16 text-white/40 border border-dashed border-white/10 rounded-2xl">
          <Youtube size={48} className="mx-auto mb-3 opacity-40"/>
          <p>Aucune vidéo pour le moment.</p>
          <p className="text-sm">Clique « Ajouter » pour poster ton 1er clip.</p>
        </div>
      ) : (
        <ul className="space-y-2">
          {videos.map(v => (
            <li key={v.video_id} className={`flex items-center gap-3 p-3 rounded-xl border ${v.is_active ? "bg-white/[0.04] border-white/10" : "bg-zinc-900/60 border-amber-500/30 opacity-60"}`}>
              <GripVertical size={14} className="text-white/30 shrink-0"/>
              <div className="w-12 h-12 rounded-lg bg-zinc-800 shrink-0 overflow-hidden">
                {v.thumbnail ? <img src={v.thumbnail} alt="" className="w-full h-full object-cover"/> : <div className="w-full h-full flex items-center justify-center text-xs text-white/40">{v.provider}</div>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-sm truncate">{v.title}</div>
                <div className="text-[10px] text-white/40 truncate">
                  {v.provider} · sort={v.sort_order} · {v.views || 0} vues
                  {v.artist_name && <> · 👤 {v.artist_name}</>}
                  {v.category && <> · {TALENT_CATS.find(c => c.key === v.category)?.label || v.category}</>}
                </div>
                {Array.isArray(v.vote_categories) && v.vote_categories.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {v.vote_categories.map(slug => (
                      <span key={slug} className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-fuchsia-400/15 text-fuchsia-200 border border-fuchsia-400/30">
                        🗳️ {slug}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <button onClick={() => toggleActive(v)} className="p-1.5 rounded-lg hover:bg-white/10" title={v.is_active ? "Masquer" : "Activer"}>
                {v.is_active ? <Eye size={14} className="text-emerald-300"/> : <EyeOff size={14} className="text-amber-400"/>}
              </button>
              <button onClick={() => setEditing(v)} className="p-1.5 rounded-lg hover:bg-white/10"><Edit size={14}/></button>
              <button onClick={() => remove(v.video_id)} className="p-1.5 rounded-lg hover:bg-red-500/20 text-red-300"><Trash2 size={14}/></button>
            </li>
          ))}
        </ul>
      )}

      {editing && <VideoEditor video={editing} onClose={() => setEditing(null)} onSave={save}/>}
    </div>
  );
};

const VideoEditor = ({ video, onClose, onSave }) => {
  const [d, setD] = useState({
    title: "", description: "", video_url: "", thumbnail: "",
    artist_id: "", track_id: "", category: "", sort_order: 100, is_active: true,
    vote_categories: [],
    ...video,
  });
  const set = (k, v) => setD(prev => ({ ...prev, [k]: v }));
  const isNew = !video?.video_id;

  // Catégories de vote TopMada (chargées depuis /topmada/categories)
  const [voteCats, setVoteCats] = useState([]);
  useEffect(() => {
    api.get("/topmada/categories")
      .then(({ data }) => setVoteCats(data.categories || []))
      .catch(() => setVoteCats([]));
  }, []);

  const toggleVoteCat = (slug) => {
    const cur = Array.isArray(d.vote_categories) ? d.vote_categories : [];
    set("vote_categories", cur.includes(slug) ? cur.filter(s => s !== slug) : [...cur, slug]);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-zinc-900 border border-white/15 rounded-2xl max-w-xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-white/10 flex items-center justify-between">
          <h3 className="font-bold flex items-center gap-2"><Youtube size={18} className="text-red-400"/> {isNew ? "Nouvelle vidéo" : "Modifier"}</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10"><X size={18}/></button>
        </div>
        <div className="p-4 space-y-3">
          <Field label="Titre *" v={d.title} on={v => set("title", v)} placeholder="Ex: Jaolah feat. Lima — Manaraka Anao" testid="tmv-title"/>

          {/* Upload direct OU URL externe */}
          <div className="bg-amber-500/5 border border-amber-400/20 rounded-xl p-3">
            <p className="text-[10px] uppercase tracking-widest text-amber-300/80 mb-2">Source vidéo (au choix)</p>
            <UploadField onUploaded={(url) => set("video_url", url)} />
            <div className="text-center text-[10px] text-white/30 my-1.5">— OU —</div>
            <Field label="Lien externe (YouTube / TikTok / Vimeo)" v={d.video_url} on={v => set("video_url", v)} placeholder="https://..." testid="tmv-url"/>
            {d.video_url && d.video_url.startsWith("/uploads/") && (
              <p className="text-[10px] text-emerald-300 mt-1">✓ Vidéo uploadée : {d.video_url.split("/").pop()}</p>
            )}
          </div>

          <Field label="Image miniature (URL, optionnel)" v={d.thumbnail} on={v => set("thumbnail", v)} placeholder="https://..." testid="tmv-thumb"/>
          <Field label="Description (optionnel)" v={d.description} on={v => set("description", v)} placeholder="Court texte qui apparaît sous la vidéo" multiline testid="tmv-desc"/>
          <div className="grid grid-cols-2 gap-2">
            <Field label="ID artiste (optionnel)" v={d.artist_id} on={v => set("artist_id", v)} placeholder="u_xxxxx" testid="tmv-artist"/>
            <div>
              <label className="text-[10px] uppercase text-white/40">Catégorie talent</label>
              <select value={d.category || ""} onChange={e => set("category", e.target.value)}
                      className="w-full mt-1 rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm"
                      data-testid="tmv-cat">
                {TALENT_CATS.map(c => <option key={c.key} value={c.key} className="bg-black">{c.label}</option>)}
              </select>
            </div>
          </div>

          {/* Catégories de vote Top Mada — multi-sélection */}
          <div className="bg-fuchsia-500/[0.06] border border-fuchsia-400/25 rounded-xl p-3">
            <div className="flex items-center justify-between mb-2">
              <label className="text-[10px] uppercase tracking-widest text-fuchsia-200 font-bold inline-flex items-center gap-1.5">
                <Tag size={12}/> Catégories de vote <span className="text-fuchsia-300/60">(multi-sélection)</span>
              </label>
              {Array.isArray(d.vote_categories) && d.vote_categories.length > 0 && (
                <span className="text-[10px] text-fuchsia-300">{d.vote_categories.length} sélectionnée{d.vote_categories.length > 1 ? "s" : ""}</span>
              )}
            </div>
            {voteCats.length === 0 ? (
              <p className="text-[11px] text-white/45 italic">Aucune catégorie disponible. Crée-en depuis l'onglet "Top Mada" → "Catégories de vote".</p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {voteCats.map(c => {
                  const selected = (d.vote_categories || []).includes(c.slug);
                  const kindEmoji = c.kind === 'topmada_video' ? '🎬' : c.kind === 'track' ? '🎵' : '🎤';
                  return (
                    <button
                      key={c.slug}
                      type="button"
                      onClick={() => toggleVoteCat(c.slug)}
                      data-testid={`tmv-vote-cat-${c.slug}`}
                      className={`px-2.5 py-1.5 rounded-full text-xs font-bold transition border ${
                        selected
                          ? "bg-fuchsia-400 text-black border-fuchsia-300 shadow-sm"
                          : "bg-white/[0.05] text-white/70 border-white/15 hover:bg-white/[0.1] hover:text-white"
                      }`}
                    >
                      {selected ? "✓ " : ""}{kindEmoji} {c.label}
                    </button>
                  );
                })}
              </div>
            )}
            <p className="text-[10px] text-white/40 mt-2 italic">
              Les votes sur ce clip compteront pour chaque catégorie sélectionnée. Affichées en badges colorés sur la page Top Mada.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Ordre (1 = en premier)" v={d.sort_order} on={v => set("sort_order", v)} type="number" testid="tmv-order"/>
            <label className="flex items-center gap-2 pt-5 text-sm">
              <input type="checkbox" checked={!!d.is_active} onChange={e => set("is_active", e.target.checked)} data-testid="tmv-active"/>
              Visible sur Top Mada
            </label>
          </div>
          <div className="flex gap-2 pt-2">
            <button onClick={() => onSave(d)} disabled={!d.title || !d.video_url}
                    data-testid="tmv-save"
                    className="flex-1 px-3 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-red-500 hover:opacity-90 disabled:opacity-50 font-bold text-sm flex items-center justify-center gap-2">
              <Save size={14}/> Enregistrer
            </button>
            <button onClick={onClose} className="px-4 py-2.5 rounded-xl bg-white/10 hover:bg-white/20 text-sm">Annuler</button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, v, on, placeholder, type = "text", multiline = false, testid }) => (
  <div>
    <label className="text-[10px] uppercase text-white/40">{label}</label>
    {multiline ? (
      <textarea value={v || ""} onChange={e => on(e.target.value)} placeholder={placeholder} rows={2}
                data-testid={testid}
                className="w-full mt-1 rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm focus:outline-none focus:border-amber-400/60"/>
    ) : (
      <input type={type} value={v || ""} onChange={e => on(type === "number" ? parseInt(e.target.value, 10) || 0 : e.target.value)} placeholder={placeholder}
             data-testid={testid}
             className="w-full mt-1 rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm focus:outline-none focus:border-amber-400/60"/>
    )}
  </div>
);

const UploadField = ({ onUploaded }) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 200 * 1024 * 1024) { toast.error("Vidéo trop lourde (max 200 MB)"); return; }
    setUploading(true); setProgress(0);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/admin/topmada/videos/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => e.total && setProgress(Math.round((e.loaded / e.total) * 100)),
      });
      if (data?.video_url) { onUploaded(data.video_url); toast.success("Vidéo uploadée ✨"); }
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload échoué"); }
    finally { setUploading(false); setProgress(0); }
  };
  return (
    <label className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 border border-dashed border-white/20 cursor-pointer transition" data-testid="tmv-upload-label">
      <Upload size={14} className="text-amber-300"/>
      <span className="text-xs flex-1">
        {uploading ? `Upload ${progress}%…` : "📤 Cliquer pour uploader un fichier (MP4 / WebM / MOV, max 200 MB)"}
      </span>
      <input type="file" accept="video/mp4,video/webm,video/quicktime,.mov,.m4v" hidden onChange={handle} disabled={uploading} data-testid="tmv-upload-input"/>
    </label>
  );
};
